import AsyncSelect, {
  Option,
  type AsyncSelectBaseProps,
} from "@/components/AsyncSelect";
import appConfiguration from "@/components/cfg/AppCfg";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { EntityListLink } from "@/components/EntityListLink";
import { Module } from "@/constants";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { FilterType } from "@/lib/filters";
import { PayrollDepartmentService } from "@/services/payrollDepartmentsService";
import { ReadPayrollDepartment } from "@/types/payrollDepartment";
import { ComponentProps, forwardRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

interface PayrollDepartmentSelectProps extends AsyncSelectBaseProps {
  className?: string;
  inDialog?: boolean;
  showEntityLink?: boolean;
  defaultItem?: Partial<Option>;
}

export default forwardRef<HTMLButtonElement, PayrollDepartmentSelectProps>(
  function PayrollDepartmentSelect(
    { value, inDialog, showEntityLink = true, defaultItem, ...props },
    ref,
  ) {
    const { t } = useTranslation();
    const name = Module.PayrollDepartments;
    const options = useCreateDataTableOptions(name);
    const queryKey = useDynamicQueryParams<ReadPayrollDepartment>(
      name,
      options,
    );

    const onFilter: ComponentProps<typeof AsyncSelect>["onChange"] =
      useCallback(
        (filterValue) => {
          options.setFilterValue("name", {
            value: filterValue,
            filter: FilterType.IncludesString,
          });
        },
        [options],
      );

    return (
      <span className="flex items-center gap-1">
        <AsyncSelect
          queryKey={queryKey}
          queryFn={() =>
            PayrollDepartmentService.getList({
              filters: options.filters,
              limit: appConfiguration.selectComponentsPaginationLimit,
            })
          }
          placeholder={t("payrollDepartmentSelect.placeholder")}
          loadingLabel={t("payrollDepartmentSelect.loadingDepartments")}
          noResultsLabel={t("payrollDepartmentSelect.noResults")}
          searchLabel={t("payrollDepartmentSelect.search")}
          enableFilter
          {...props}
          onFilter={onFilter}
          value={value ? value.toString() : undefined}
          ref={ref}
          inDialog={inDialog}
          defaultItem={defaultItem}
        />
        {showEntityLink && (
          <EntityListLink
            path={Module.PayrollDepartments}
            label={t("payrollDepartmentSelect.link")}
          />
        )}
      </span>
    );
  },
);
